import {FC, useMemo, useCallback, RefObject, useEffect, useRef, useState} from 'react';
import {
    createEditor,
    Element,
    Editor,
    Descendant,
} from 'slate'
import {withHistory} from 'slate-history'
import {
    DefaultElement,
    Editable,
    RenderElementProps,
    RenderLeafProps,
    Slate,
    withReact,
} from 'slate-react'
import {deserialize, serialize} from "../lib/serialization.ts";
import withPasteHTML from "../lib/withPasteHTML.ts";
import withStyles from "../lib/withStyles.ts";
import withHotkeys from "../lib/withHotkeys.ts";

export type EditorProps = {
    ref?: RefObject<Editor | null>
    contents?: string;
    onChange?: (html: string) => void
    onChangeSelection?: (e: Element) => void
}

const ElementComponent = (props: RenderElementProps) => {
    switch (props.element.type) {
        case "paragraph":
            return <p {...props.attributes}>{props.children}</p>;
        case "h1":
            return <h1 {...props.attributes}>{props.children}</h1>;
        case "h2":
            return <h2 {...props.attributes}>{props.children}</h2>;
        case "h3":
            return <h3 {...props.attributes}>{props.children}</h3>;
        default:
            // For the default case, we delegate to Slate's default rendering.
            return <DefaultElement {...props}/>;
    }
}

const LeafComponent = ({attributes, children, leaf}: RenderLeafProps) => {
    if (leaf.bold) {
        children = <strong>{children}</strong>
    }
    if (leaf.italic) {
        children = <em>{children}</em>
    }
    if (leaf.underline) {
        children = <u>{children}</u>
    }

    return <span {...attributes}>{children}</span>
}

const EditorComponent: FC<EditorProps> = ({ref, contents, onChange, onChangeSelection}) => {
    const [editor, setEditor] = useState<Editor | null>(null)
    useEffect(() => {
        setEditor(withHotkeys(withStyles(withPasteHTML(withHistory(withReact(createEditor()))))))
    }, [])
    const intermediateValue = useRef<Descendant[] | null>(null)

    useEffect(() => {
        if (ref) {
            ref.current = editor
        }
    }, [editor, ref])

    const initialValue: Descendant[] = useMemo(
        () => {
            const result = deserialize(contents ?? "")
            return result.length > 0 ? result : [{type: 'paragraph', children: [{text: ''}]}]
        },
        [contents]
    )

    const handleChange = useCallback((value: Descendant[]) => {
        intermediateValue.current = value
    }, [])

    const handleSelectionChange = useCallback(() => {
        const selectedElement = editor?.getSelectedElement()
        if (selectedElement) {
            onChangeSelection?.(selectedElement)
        }
    }, [editor, onChangeSelection])

    const handleBlur = useCallback(() => {
        if (intermediateValue.current != null) {
            onChange?.(serialize(intermediateValue.current))
        }
    }, [onChange])

    const renderElement = useCallback(
        (props: RenderElementProps) => <ElementComponent {...props} />,
        []
    )
    const renderLeaf = useCallback(
        (props: RenderLeafProps) => <LeafComponent {...props} />,
        []
    )

    return editor ? (
        <Slate editor={editor} initialValue={initialValue} onChange={handleChange} onSelectionChange={handleSelectionChange}>
            <Editable
                placeholder="Type hier uw verhaal..."
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                spellCheck
                className="editable"
                onKeyDown={editor.onKeyDown}
                onBlur={handleBlur}
            />
        </Slate>
    ) : null
}

export default EditorComponent;
